import { isNil, merge } from "lodash";
import { local_schema, ILocalState$ } from "./types";
import { IMeasure } from "@/core/shared/api";
import { transforms } from "@/core/shared/transforms";
import { z } from "zod";
import { invertRecord } from "@/core/utils/reference-map";

export function validateAndMergeState(state: ILocalState$, new_state?: object) {
  if (new_state !== undefined) {
    const isValid = local_schema.safeParse(new_state);
    if (!isValid.success) {
      throw new Error(
        `Invalid props provided to createState$. Validation errors: ${JSON.stringify(isValid.error.format(), null, 2)}`,
      );
    }
    state = merge(state, new_state);
    return state;
  }
  return state;
}

export function transformMeasureScore(measure: IMeasure, measure_type: string) {
  if (measure_type === "NSS") {
    const score = transforms.nss.toPercentage(measure.measure_value);
    return score;
  } else if (measure_type === "PREVALENCE") {
    const score = transforms.prevalence.toPercentage(measure.measure_value);
    return score;
  } else if (measure_type === "RATING") {
    const score = transforms.rating.toNumber(measure.measure_value);
    return score;
  } else if (measure_type === "REVIEW_COUNT") {
    const score = transforms.count.toNumber(measure.measure_value);
    return score;
  }

  return null;
}

export function mapMeasureNameToParams(
  measureMap: Record<string, { default: string; withGeoContinent: string; withGeoCountry: string }>,
  selected_measure_id: string,
  selected_location_id: string | undefined,
  selected_location_type: string | undefined,
) {
  if (selected_location_id && selected_location_type) {
    if (selected_location_type === "continent") {
      const measure_name = measureMap[selected_measure_id];
      return measure_name.withGeoContinent;
    } else if (selected_location_type === "country") {
      const measure_name = measureMap[selected_measure_id];
      return measure_name.withGeoCountry;
    }
  }
  const measure_name = measureMap[selected_measure_id];
  return measure_name.default;
}

export function mapMeasureNameToId(
  measureMap: Record<string, { default: string; withGeoContinent: string; withGeoCountry: string }>,
  measure_name: string,
): string | undefined {
  for (const [measureId, nameObject] of Object.entries(measureMap)) {
    if (
      nameObject.default === measure_name ||
      nameObject.withGeoContinent === measure_name ||
      nameObject.withGeoCountry === measure_name
    ) {
      return measureId;
    }
  }
  return undefined; // Return undefined if no matching measure_id is found
}

export function formatChartData(props: {
  data: IMeasure[];
  selected_company_id: string;
  selected_x_measure_name: string;
  selected_y_measure_name: string;
  selected_x_measure_id: string;
  selected_y_measure_id: string;
}) {
  const {
    data,
    selected_company_id,
    selected_x_measure_name,
    selected_y_measure_name,
    selected_x_measure_id,
    selected_y_measure_id,
  } = props;

  // group by topic
  const grouped_data: Record<string, IMeasure[]> = {};
  data.forEach((measure) => {
    const topic = measure.topic_name;

    if (!grouped_data[topic]) {
      grouped_data[topic] = [];
    }
    grouped_data[topic].push(measure);
  });

  type Entry = { company_id: string; topic_name: string; x: number | null; y: number | null };
  const chart_data: Entry[] = [];

  Object.values(grouped_data).forEach((measures) => {
    let no_signal = false;
    const entry: Entry = {
      company_id: selected_company_id,
      topic_name: "",
      x: null,
      y: null,
    };
    measures.forEach((measure) => {
      entry.topic_name = measure.topic_name;
      no_signal = measure.measure_signal === "NO_SIGNAL";

      if (measure.measure_name === selected_x_measure_name) {
        entry.x = no_signal ? null : transformMeasureScore(measure, selected_x_measure_id);
      }

      if (measure.measure_name === selected_y_measure_name) {
        entry.y = no_signal ? null : transformMeasureScore(measure, selected_y_measure_id);
      }
    });
    if (isNil(entry.x) || isNil(entry.y)) {
      return;
    }
    chart_data.push(entry);
  });
  return chart_data;
}

export function formatTableData(props: {
  data: IMeasure[];
  selected_x_measure_name: string;
  selected_y_measure_name: string;
  selected_x_measure_id: string;
  selected_y_measure_id: string;
  measures_config: z.infer<typeof local_schema>["config"]["measure"]["measures"];
  measure_map: z.infer<typeof local_schema>["config"]["company"]["measure_filter_to_measure_name_map"];
}) {
  const {
    data,
    selected_x_measure_name,
    selected_y_measure_name,
    selected_x_measure_id,
    selected_y_measure_id,
    measure_map,
    measures_config,
  } = props;

  const what = mapMeasureNameToId(measure_map, selected_x_measure_name);

  // group by measure_name
  const grouped_data: Record<string, IMeasure[]> = {};
  data.forEach((measure) => {
    const measure_name = measure.measure_name;
    if (!grouped_data[measure_name]) {
      grouped_data[measure_name] = [];
    }
    grouped_data[measure_name].push(measure);
  });

  type TableCell = {
    id: string;
    score: number | string | null;
    signal?: string;
    label: string;
  };

  type TableRow = Record<string, TableCell>;

  const table_data: TableRow[] = [];

  Object.entries(grouped_data).forEach(([measure_name, measures]) => {
    const measure_id = mapMeasureNameToId(measure_map, measure_name);
    const measure_config = measures_config.find((m) => m.id === measure_id);
    const row_entry: TableRow = {
      measure: {
        id: measure_name,
        score: null,
        label: measure_config?.label + (measure_config?.unit ? ` (${measure_config?.unit})` : ""),
        signal: undefined,
      },
    };
    measures.forEach((measure) => {
      const no_signal = measure.measure_signal === "NO_SIGNAL";
      const entry: TableCell = {
        id: measure.topic_name,
        score: null,
        label: measure_config?.label ?? "Unknown Measure",
        signal: measure.measure_signal,
      };

      if (measure.measure_name === selected_x_measure_name) {
        entry.score = no_signal ? null : transformMeasureScore(measure, selected_x_measure_id);
      }

      if (measure.measure_name === selected_y_measure_name) {
        entry.score = no_signal ? null : transformMeasureScore(measure, selected_y_measure_id);
      }
      row_entry[measure.topic_name] = entry;
    });
    table_data.push(row_entry);
  });
  return table_data;
}
